<template>
    <q-page :padding="!transfer.reference && $q.screen.gt.xs" class="docs-table" :style-fn="myTweak">
        <q-table
            title="Incoming transactions"
            ref="table"
            :data="serverData"
            :columns="columns"
            :filter="filter"
            row-key="id"
            :pagination.sync="serverPagination"
            :visible-columns="visibleColumns"
            :loading="loading"
            :selection="selection"
            :selected.sync="selectedRows"
            :rows-per-page-options="[10, 15, 30, 50, 100]"
            @request="all"
        >
            <template v-slot:top-selection="props" v-if="$auth.can('transactions.incoming.edit')">
                <q-btn color="negative" outline icon="fa fa-trash" :label="$t('delete')" class="q-mr-sm"
                       @click="deleteRows"/>
                <q-btn color="secondary" outline icon="fa fa-edit" :label="$t('edit')" class="q-mr-sm"
                       v-if="selectedRows.length === 1" @click="edit(selectedRows[0])"/>
                <q-btn color="pink-5" outline icon="fa fa-times" class="q-mr-sm" @click="selectedRows = []"/>
            </template>

            <template v-slot:top-right="props">
                <q-btn
                    outline
                    label="New"
                    icon="fa fa-plus"
                    color="positive"
                    class="q-mr-sm"
                    v-if="user_can_create"
                    @click="makeNewTransaction"
                />
                <q-btn outline :label="$t('refresh')" icon="fas fa-sync" color="primary" class="q-mr-sm"
                       @click="all({pagination: serverPagination, filter})"
                       :loading="loading"
                >
                    <template v-slot:loading>
                        <q-spinner-dots/>
                    </template>
                </q-btn>
                <q-btn
                    :outline="!is_filtered"
                    color="black"
                    :label="is_filtered ? 'Filtered' : 'Filter'"
                    class="q-mr-sm"
                    icon="fa fa-filter"
                >
                    <q-menu>
                        <div class="row q-pa-md q-col-gutter-md" style="width: 300px">
                            <div class="col-12">
                                <q-input
                                    :value="date_range.from ? date_range.from + ' | ' + date_range.to : ''"
                                    dense
                                    class="q-mr-sm"
                                    color="primary"
                                    label="Date range"
                                    readonly
                                >
                                    <template v-slot:append v-if="date_range.from" >
                                        <q-icon
                                            name="cancel"
                                            @click.stop="removeDateRangeFilter"
                                            class="cursor-pointer"
                                        />
                                    </template>
                                    <template v-slot:prepend>
                                        <q-icon name="event" class="cursor-pointer">
                                            <q-popup-proxy
                                                ref="qDateProxy"
                                                transition-show="scale"
                                                transition-hide="scale"
                                                @hide="all({ pagination: serverPagination, filter})"
                                            >
                                                <q-date
                                                    v-model="selecting_range"
                                                    range
                                                    mask="YYYY-MM-DD"
                                                >
                                                    <div class="row items-center justify-end">
                                                        <q-btn v-close-popup label="Set" color="primary" flat />
                                                    </div>
                                                </q-date>
                                            </q-popup-proxy>
                                        </q-icon>
                                    </template>
                                </q-input>
                            </div>
                            <div class="col-12">
                                <q-select
                                    v-model="currency"
                                    dense
                                    options-dense
                                    emit-value
                                    map-options
                                    :options="incoming_currencies"
                                    option-value="acronym"
                                    option-label="name"
                                    style="min-width: 100px; margin-right:10px"
                                    class="q-ml-sm"
                                    @input="all({pagination: serverPagination, filter: filter})"
                                    color="primary"
                                />
                            </div>
                            <div class="col-12">
                                <q-select
                                    v-model="rebit_account"
                                    label="Rebit's account"
                                    dense
                                    clearable
                                    options-dense
                                    emit-value
                                    map-options
                                    use-input
                                    :options="filtered_rebit_accounts"
                                    option-value="id"
                                    option-label="label"
                                    color="primary"
                                    @input="all({pagination: serverPagination, filter: filter})"
                                    @filter="filterRebitAccounts"
                                >
                                    <template v-slot:option="scope">
                                        <q-item
                                            v-bind="scope.itemProps"
                                            v-on="scope.itemEvents"
                                            class="q-py-sm"
                                        >
                                            <q-item-section>
                                                <q-item-label>{{ scope.opt.label }}</q-item-label>
                                            </q-item-section>
                                        </q-item>
                                    </template>
                                </q-select>
                            </div>
                            <div class="col-12">
                                <q-select
                                    v-model="filtered_statuses"
                                    multiple
                                    dense
                                    options-dense
                                    :display-value="filtered_statuses.length === 9 ? 'Statuses' : 'Statuses (filtered)'"
                                    emit-value
                                    map-options
                                    :options="statuses"
                                    style="min-width: 150px;margin-right:10px"
                                    @blur="all({ pagination: serverPagination, filter})"
                                    color="primary"
                                >
                                    <template v-slot:option="{ itemProps, itemEvents, opt, selected, toggleOption }">
                                        <q-item
                                            v-bind="itemProps"
                                            v-on="itemEvents"
                                        >
                                            <q-item-section>
                                                <q-item-label v-html="opt.label" ></q-item-label>
                                            </q-item-section>
                                            <q-item-section side>
                                                <q-toggle :value="selected" @input="toggleOption(opt)" color="green"/>
                                            </q-item-section>
                                        </q-item>
                                    </template>
                                </q-select>
                            </div>
                        </div>
                    </q-menu>
                </q-btn>
                <q-input dense debounce="300" v-model="filter" :placeholder="$t('search')" class="q-mr-sm"
                         v-if="!transfer.reference">
                    <template v-slot:prepend>
                        <q-icon name="search"/>
                    </template>
                </q-input>
                <q-select
                    v-model="visibleColumns"
                    multiple
                    dense
                    options-dense
                    :display-value="$q.lang.table.columns"
                    emit-value
                    map-options
                    :options="columns"
                    option-value="name"
                    style="min-width: 100px;margin-right:10px"
                    class="q-ml-sm"
                    color="primary"
                />
            </template>

            <!-- START CUSTOM COLUMNS -->
            <template v-slot:body="props">
                <q-tr :props="props"
                      :statement="props.row.currentStatus = statuses.find(item => item.value === props.row.status)">
                    <q-td auto-width>
                        <q-checkbox dense v-model="props.selected" />
                    </q-td>
                    <q-td key="id" :props="props">{{ props.row.id }}</q-td>
                    <q-td key="status" :props="props">
                        <div>
                            <q-popup-edit v-model="props.row.status" buttons
                                          @save="(val, initialValue) => editInline(val, initialValue, props.row, 'status')"
                                          v-if="incoming_currencies_acronyms.includes(props.row.currency_acronym)"
                            >
                                <template v-slot="{ initialValue, value, emitValue, validate, set, cancel }">
                                    <q-btn-dropdown
                                        :label="props.row.currentStatus.label"
                                        :color="props.row.currentStatus.color"
                                        outline
                                        dense
                                    >
                                        <q-list bordered separator>
                                            <q-item clickable v-close-popup v-for="status in statuses" @click="props.row.status = status.value" :key="status.value">
                                                <q-item-section>
                                                    <q-item-label :class="'text-'+status.color">{{ status.label }}</q-item-label>
                                                </q-item-section>
                                            </q-item>
                                        </q-list>
                                    </q-btn-dropdown>
                                </template>
                            </q-popup-edit>
                            <q-chip
                                square
                                :outline="!incoming_currencies_acronyms.includes(props.row.currency_acronym)"
                                :color="props.row.currentStatus.color"
                                text-color="white"
                                :style="incoming_currencies_acronyms.includes(props.row.currency_acronym) ? 'cursor: pointer' : ''"
                            >{{ props.row.currentStatus.label }}
                            </q-chip>
                        </div>
                    </q-td>
                    <q-td key="payment_details" :props="props">
                        <q-item class="q-pa-none">
                            <q-item-section avatar>
                                <q-btn dense round flat size="md" color="primary" icon="fa fa-file-invoice" @click="openReceipts(props.row)" v-if="props.row.has_receipt" />
                                <q-btn dense round flat size="md" color="primary" icon="fa fa-file-arrow-up" @click="openUploadReceipts(props.row)" v-else-if="props.row.transfer_reference" />
                            </q-item-section>
                            <q-item-section>
                                <q-item-label lines="1">
                                    {{props.row.payment_details}}
                                </q-item-label>
                                <q-item-label>
                                    <currency-format
                                        class="text-bold inline-block"
                                        :value="props.row.amount"
                                        :unit="props.row.currency"
                                        :decimal-max-length="4"
                                        hide-white-spaces
                                    />
                                </q-item-label>
                            </q-item-section>
                        </q-item>
                    </q-td>
                    <q-td key="rebit_account" :props="props">
                        <div v-if="$auth.can('rebit-accounts.list')">
                            <q-item>
                                <q-item-section side>
                                    <q-btn icon="fa fa-link" size="xs" color="info" round flat
                                           @click="$router.push('/rebit-accounts?q='+props.row.rebit_account.label)"/>
                                </q-item-section>
                                <q-item-section>
                                    <q-item-label class="text-bold">{{ props.row.rebit_account.label }}</q-item-label>
                                </q-item-section>
                            </q-item>
                        </div>
                        <div v-else>
                            <q-item>
                                <q-item-section class="text-bold">
                                    <q-item-label>{{ props.row.rebit_account.label }}</q-item-label>
                                </q-item-section>
                            </q-item>
                        </div>
                    </q-td>
                    <q-td key="references" :props="props">
                        <div>
                            {{props.row.references}}
                            <q-tooltip anchor="top middle" self="center middle" content-class="bg-amber-2 text-black" content-style="font-size: 13px">
                                {{props.row.references}}
                            </q-tooltip>
                        </div>
                    </q-td>
                    <q-td key="description" :props="props">
                        <div>
                            {{props.row.description}}
                            <q-tooltip anchor="top middle" self="center middle" content-class="bg-amber-2 text-black" content-style="font-size: 13px">
                                {{props.row.description}}
                            </q-tooltip>
                        </div>
                    </q-td>
                    <q-td key="transfer_reference" :props="props">
                        <div v-if="$auth.can('transfers.list') && props.row.transfer_reference">
                            <q-btn icon="fa fa-link" size="xs" color="info" round flat @click.native="openURL('/transfers?q='+props.row.transfer_reference)"/>
                            {{ props.row.transfer_reference}}
                        </div>
                        <div v-else>{{ props.row.transfer_reference || '-'}}</div>
                    </q-td>
                    <q-td key="match_code" :props="props">
                        <div v-if="$auth.can('transfers.list') && props.row.match_code">
                            <q-btn icon="fa fa-link" size="xs" color="info" round flat @click.native="openURL('/transfers?q='+props.row.match_code)"/>
                            {{props.row.match_code}}
                        </div>
                        <div v-else>{{props.row.match_code || '-'}}</div>
                    </q-td>
                    <q-td key="bank_date" :props="props">
                        {{ props.row.bank_date }}
                    </q-td>
                    <q-td key="created_at" :props="props">
                        {{ props.row.created_at }}
                        <q-tooltip
                            anchor="top middle"
                            self="center middle"
                            content-class="bg-amber-2 text-black"
                            content-style="font-size: 13px"
                        >{{props.row.created_at_utc}} (UTC)</q-tooltip>
                    </q-td>
                    <q-td key="incoming_payment_method" :props="props">
                        {{ props.row.incoming_payment_method }}
                    </q-td>
                    <q-td key="operator" :props="props">
                        {{ props.row.operator }}
                    </q-td>
                    <q-td key="txid" :props="props">
                        {{ props.row.txid }}
                    </q-td>
                    <q-td key="setting" :props="props"
                          v-if="$auth.can('transactions.incoming.edit')">
                        <q-btn round size="sm" color="orange" icon="fa fa-edit" @click="edit(props.row)" :disabled="!incoming_currencies_acronyms.includes(props.row.currency_acronym)"/>&nbsp;
                        <q-btn round size="sm" color="red" icon="fa fa-trash" @click="deleteRow(props.row)" :disabled="!incoming_currencies_acronyms.includes(props.row.currency_acronym)"/>
                    </q-td>
                </q-tr>
            </template>
            <!-- END CUSTOM COLUMNS -->

            <template v-slot:bottom-row="props">
                <tr class="text-green text-bold">
                    <td class="total-row-borders" colspan="3">{{$t('total')}}</td>
                    <td class="total-row-borders text-right">
                        <currency-format
                            class="inline-block"
                            :value="total_amount"
                            :unit="selected_currency_symbol"
                            :decimal-max-length="4"
                            hide-white-spaces
                        />
                    </td>
                    <td class="total-row-borders" colspan="10"></td>
                </tr>
                <tr class="text-bold" v-if="serverData.length && Object.keys(transfer).length">
                    <td class="total-row-borders" colspan="11">
                        <div class="q-py-sm text-body1 text-green">
                            Total accepted payments:&nbsp;
                            <currency-format
                                class="text-bold inline-block"
                                :value="total_accepted_payments"
                                :unit="transfer.origin_currency"
                                :decimal-max-length="4"
                                hide-white-spaces
                            />
                        </div>
                        <div class="q-py-sm text-body1" :class="outstanding_amount === 0 ? 'text-grey' : 'text-orange'">
                            Outstanding amount:&nbsp;
                            <currency-format
                                class="text-bold inline-block"
                                :value="outstanding_amount"
                                :unit="transfer.origin_currency"
                                :decimal-max-length="4"
                                hide-white-spaces
                            />
                            {{total_accepted_payments > transfer.paid_amount ? '(overpayment)' : ''}}
                        </div>
                    </td>
                </tr>
            </template>
        </q-table>

        <incoming-transaction-modal
            v-model="displayTransactionModal"
            :reference="transfer.reference"
            @created="refresh"
            @updated="updateTransaction"
            :transaction-id="this.selectedRows.length ? this.selectedRows[0].id : 0"
        />

        <receipts-dialog
            v-model="displayReceipts"
            :reference="selectedRow.transfer_reference"
            :transaction-id="selectedRow.id"
            show-upload-button
            @upload="openUploadRecipientDialog"
            v-if="selectedRow.transfer_reference"
        />

        <upload-receipts-dialog
            :key="Math.random()"
            v-model="displayUploadReceiptsDialog"
            :transfer-reference="selectedRow.transfer_reference"
            :exist-receipts="existReceipts"
            :txid="selectedRow.id"
            @uploaded="selectedRow.has_receipt = true"
        >
            <template v-slot:header>
                <div class="text-h6">Send Payment Receipt for <currency-format
                    :value="selectedRow.amount"
                    :unit="selectedRow.currency"
                    hide-zero-decimal
                    hide-white-spaces
                    class="inline-block" />
                </div>
                <q-space />
                <q-btn icon="close" flat round dense v-close-popup />
            </template>

            <div class="q-py-md">
                Please upload receipt for each transaction separately.
            </div>
        </upload-receipts-dialog>
    </q-page>
</template>

<script>
import Transaction from '../models/Transaction.js'
import IncomingTransactionModal from "../components/IncomingTransactionModal"
import {mapGetters, mapActions} from 'vuex'
import { date, openURL } from 'quasar'
import ReceiptsDialog from "Modules/Transfer/resources/components/ReceiptsDialog.vue"
import UploadReceiptsDialog from "Modules/Transfer/resources/components/history/detail-tabs/partials/PaymentReceiptsDialog.vue"

export default {
    name: 'IncomingTransactions',
    props: {
        transfer: {
            type: Object,
            default: () => ({})
        }
    },
    computed: {
        ...mapGetters(['incoming_currencies', 'rebit_accounts', 'allRebitAccountsByCurrency']),
        user_can_create() {
            return this.$auth.can('transactions.incoming.create')
        },
        incoming_currencies_acronyms() {
            return this.incoming_currencies.map(item => item.acronym)
        },
        columns() {
            let columns = [
                { name: 'id', required: true, label: this.$t('transactions.table_titles.id'), align: 'left', field: 'id', sortable: true },
                { name: 'status', label: this.$t('transactions.table_titles.status'), align: 'left', field: 'status', sortable: true },
                { name: 'payment_details', label: this.$t('transactions.table_titles.payer'), align: 'left', field: 'payment_details', sortable: false },
                { name: 'rebit_account', label: this.$t('transactions.table_titles.rebit_account'), align: 'left', field: 'rebit_account.number', sortable: false },
                { name: 'references', label: this.$t('transactions.table_titles.references'), align: 'left', field: 'references', sortable: true, classes: 'ellipsis', style: 'max-width: 180px', headerStyle: 'max-width: 180px' },
                { name: 'description', label: this.$t('transactions.table_titles.description'), align: 'left', field: 'description', sortable: false, classes: 'ellipsis', style: 'max-width: 180px', headerStyle: 'max-width: 180px' },
                { name: 'transfer_reference', label: this.$t('transactions.table_titles.transfer_reference'), align: 'center', field: 'transfer_reference', sortable: false },
                {name: 'match_code', label: this.$t('transactions.table_titles.match_code'), align: 'center', field: 'match_code', sortable: false},
                { name: 'bank_date', label: this.$t('transactions.table_titles.bank_date'), align: 'left', field: 'bank_date', sortable: false },
                { name: 'created_at', label: this.$t('transactions.table_titles.received_date'), align: 'left', field: 'created_at', sortable: false },
                { name: 'incoming_payment_method', label: this.$t('transactions.table_titles.payment_method'), align: 'left', field: 'incoming_payment_method', sortable: false },
                { name: 'operator', label: this.$t('transactions.table_titles.operator'), align: 'left', field: 'operator', sortable: false },
                { name: 'txid', label: this.$t('transactions.table_titles.txid'), align: 'left', field: 'txid', sortable: false },
            ]

            // add Edit/Delete column just if the user has appropriate permission
            if (this.$auth.can('transactions.incoming.edit')) {
                this.selection = 'multiple'
                this.visibleColumns.push('setting')
                columns.push({
                    name: 'setting',
                    align: 'center',
                    label: this.$t('transactions.table_titles.setting'),
                    sortable: false,
                    field: 'setting'
                })
            }
            return columns;
        },
        selecting_range: {
            get: function() {
                return this.date_range
            },
            set: function(value) {
                if(value && typeof value === 'string') {
                    this.date_range = {from: value, to: value}
                } else if(value && typeof value === 'object' && value.from) {
                    this.date_range = value
                } else {
                    this.date_range = {from: this.fifteenDaysAgo, to: this.today}
                }
            }
        },
        is_filtered() {
            return this.date_range.from || this.filtered_statuses.length !== 9 || this.rebit_account || this.currency
        },
        total_accepted_payments() {
            return this.serverData.filter(record => record.status === 'accepted').reduce((a, b) => a + b.amount, 0)
        },
        outstanding_amount() {
            return this.transfer.paid_amount ?(this.transfer.paid_amount - this.total_accepted_payments).fixZero() : 0
        },
        incomingUrl() {
            let currency = `currency=${this.currency}`
            let rebit_account = `rebit_account=${this.rebit_account ? this.rebit_account : ''}`
            let date_range = `date_range=${this.date_range.from},${this.date_range.to}`
            let statuses = `statuses=${this.filtered_statuses}`
            return `${currency}&${rebit_account}&${date_range}&${statuses}`
        },
        selected_currency_symbol() {
            return this.serverData.length ? this.serverData[0]['currency'] : ''
        },
        today() {
            const today = Date.now();
            return date.formatDate(today, 'YYYY-MM-DD')
        },
        fifteenDaysAgo() {
            const today = Date.now();
            let newDate = date.subtractFromDate(today, { days: 15})
            return date.formatDate(newDate, 'YYYY-MM-DD')
        },
    },
    data: () => ({
        separator: 'horizontal',
        filter: '',
        loading: false,
        serverPagination: {
            page: 1,
            sortBy: 'id',
            rowsPerPage: 50,
            descending: true,
            rowsNumber: 10 // specifying this determines pagination is server-side
        },
        visibleColumns: ['id', 'status', 'payment_details', 'rebit_account', 'references', 'description', 'transfer_reference', 'match_code', 'created_at'],
        serverData: [],
        selection: 'none',
        selectedRows: [],
        displayTransactionModal: false,
        currency: undefined,
        total_amount: 0,
        rebit_account: undefined,
        statuses: [
            {label: 'Pending', value: 'pending', color: 'grey'},
            {label: 'Accepted', value: 'accepted', color: 'positive'},
            {label: 'Declined', value: 'declined', color: 'negative'},
            {label: 'Refunded', value: 'refunded', color: 'negative'},
            {label: 'Rejected', value: 'rejected', color: 'negative'},
            {label: 'Frozen', value: 'frozen', color: 'warning'},
            {label: 'Require AML document', value: 'require_aml_document', color: 'warning'},
            {label: 'Clarity around name mismatch', value: 'clarity_around_name_mismatch', color: 'warning'},
            {label: 'Unknown', value: 'unknown', color: 'grey-10'},
        ],
        filtered_statuses: ['pending', 'accepted', 'declined', 'refunded', 'rejected', 'frozen','require_aml_document', 'clarity_around_name_mismatch', 'unknown'],
        date_range: {from: '', to: ''},
        selectedRow: {
            id: 0,
            transfer_reference: ''
        },
        displayReceipts: false,
        displayUploadReceiptsDialog: false,
        existReceipts: 0,
        filtered_rebit_accounts: []
    }),
    methods: {
        openURL,
        ...mapActions(['set_exchangeable_currencies', 'set_rebit_accounts']),
        all({pagination, filter}) {
            this.selectedRows = []
            this.loading = true
            Transaction.incoming(`${this.incomingUrl}&sort=${pagination.sortBy}%7C${pagination.descending ? 'desc' : 'asc'}&page=${pagination.page}&per_page=${pagination.rowsPerPage}&filter=${filter}`, ({data, meta, total_amount}) => {
                this.serverPagination = pagination
                // we also set (or update) rowsNumber
                this.serverPagination.rowsNumber = meta.total
                this.serverData = data
                this.total_amount = total_amount
                this.loading = false
            }).catch(error => {
                this.$q.notify({
                    message: error.data.error,
                    color: 'negative',
                    icon: 'fa fa-exclamation-triangle'
                })
                this.loading = false
            })
        },
        removeDateRangeFilter() {
            this.date_range = {from: '', to: ''}
            this.all({
                pagination: this.serverPagination,
                filter: this.filter
            })
        },
        edit(row) {
            this.selectedRows = []
            this.selectedRows.push(row)
            this.displayTransactionModal = true
        },
        deleteRow(row) {
            this.selectedRows.push(row)
            this.deleteRows();
        },
        deleteRows() {
            let isThirdParty = (this.selectedRows.length === 1) && (this.selectedRows[0].rebit_account.label.includes('Third-party'))

            this.$q.dialog({
                title: 'Delete information',
                message: isThirdParty ? 'Deleting this transaction and its Third-party pair. Are you sure?' : 'Are you sure?',
                ok: 'Yes',
                cancel: 'No'
            }).onOk(() => {
                this.$q.loading.show()
                this.selectedRows.map((row) => {
                    const transaction = new Transaction({id: row.id, side: 'credit'});
                    transaction.destroy(() => {
                        this.serverData = this.serverData.filter(record => record.id !== row.id);
                        this.selectedRows = this.selectedRows.filter(member => member.id !== row.id);
                        if (!this.selectedRows.length) {
                            this.$q.loading.hide()
                        }
                        this.$emit('change')
                    }).catch(({data}) => {
                        this.$q.notify(data.error)
                        this.$q.loading.hide()
                    })
                });
            }).onCancel(() => {
                this.$q.loading.hide()
                this.selectedRows = []
            })
        },
        makeNewTransaction() {
            this.selectedRows = []
            this.displayTransactionModal = true
        },
        updateTransaction(data) {
            let row = this.selectedRows[0]
            for (let field in data) {
                row[field] = data[field]
            }
            this.selectedRows = []
            this.$emit('change')
        },
        refresh() {
            this.transfer.status = undefined
            this.all({pagination: this.serverPagination, filter: this.filter})
            this.$emit('change')
        },
        myTweak(offset) {
            return {minHeight: offset ? `calc(35vh - ${offset}px)` : '35vh'}
        },
        preload() {
            this.currency = this.incoming_currencies[0]['acronym']
            this.all({
                pagination: this.serverPagination,
                filter: this.filter
            })
        },
        editInline(val, initVal, row, col) {
            row[col] = val
            this.loading = true
            new Transaction({
                side: 'credit',
                id: row.id,
                status: col === 'status' ? val : row.status,
            }).updateInline((data) => {
                for (let field in data) {
                    row[field] = data[field]
                }
                this.loading = false
            }).catch(({data}) => {
                row[col] = initVal
                this.loading = false
            })
        },
        openReceipts(row) {
            this.displayReceipts = true
            this.selectedRow = row
        },
        openUploadReceipts(row) {
            this.displayUploadReceiptsDialog = true
            this.selectedRow = row
        },
        openUploadRecipientDialog() {
            this.displayReceipts = false
            this.openUploadReceipts(this.selectedRow)
        },
        filterRebitAccounts(val, update, abort) {
            if (val === '') {
                update(() => {
                    this.filtered_rebit_accounts = this.allRebitAccountsByCurrency(this.currency)
                })
                return
            }

            update(() => {
                const needle = val.toLowerCase()
                this.filtered_rebit_accounts = this.allRebitAccountsByCurrency(this.currency).filter(account => account.label.toLowerCase().indexOf(needle) > -1)
            })
        },
    },
    beforeCreate() {
        if (!this.$auth.can('transactions.incoming')) {
            this.$router.push({name: 'dashboard'})
        }
    },
    created() {
        this.date_range = this.transfer.reference ?
            {from: "", to: ""} :
            {from: this.fifteenDaysAgo, to: this.today}
    },
    mounted() {
        // once mounted, we need to trigger the initial server data fetch
        if (this.$router.currentRoute.query.q !== undefined && !this.$parent.name) {
            this.filter = this.$router.currentRoute.query.q
        } else if (this.transfer.reference !== undefined) {
            this.currency = this.transfer.origin_currency_acronym
            this.filter = this.transfer.reference
        } else if (Object.keys(this.incoming_currencies).length === 0) {
            this.loading = true
            this.set_exchangeable_currencies().then(currencies => {
                this.loading = false
                this.preload()
            }).catch(error => {
                this.loading = false
            })
        } else {
            this.preload()
        }

        if (this.transfer.reference !== undefined) {
            this.visibleColumns.splice(this.visibleColumns.indexOf('transfer_reference'), 1)
        } else {
            this.visibleColumns.splice(this.visibleColumns.indexOf('match_code'), 1)
        }

        if (!this.rebit_accounts.length) {
            this.loading = true
            this.set_rebit_accounts().then(() => {
                this.loading = false
            }).catch(() => {
                this.loading = false
            })
        }
    },
    components: {
        ReceiptsDialog,
        UploadReceiptsDialog,
        IncomingTransactionModal
    }
}
</script>
<style>
    .total-row-borders {
        border-top: 3px double rgb(204, 204, 204) !important;
        border-bottom: 3px double rgb(204, 204, 204) !important;
    }
</style>
